import type { PdlMechanismSummary } from './pdlAdjudicatorCopy';
import {
  formatPdlScoringChip,
  PDL_BINDING,
  PDL_WATERFALL_BAR,
} from './pdlAdjudicatorUiCopy';

export type PdlWaterfallLayer = 'observation' | 'policy' | 'decision';

export type PdlWaterfallBar = {
  id: string;
  label: string;
  sublabel: string | null;
  amount: number;
  layer: PdlWaterfallLayer;
  /** Visual role within the waterfall */
  kind: 'requested' | 'table_ceiling' | 'bank_ceiling' | 'final_offer';
  binding: boolean;
};

export type PdlCapacityWaterfallModel = {
  bars: PdlWaterfallBar[];
  scaleMax: number;
  bindingHeadline: string | null;
  bindingDetail: string | null;
  /** Shorter headline detail when bank row is collapsed. */
  bindingDetailShort: string | null;
  scoringChips: string[];
  showBankPath: boolean;
};

export function parsePdlCurrency(value: string | null | undefined): number | null {
  if (!value?.trim()) return null;
  const n = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function bindingHeadline(binding: PdlMechanismSummary['binding']): string | null {
  if (binding === 'legacy') return PDL_BINDING.headlineLegacy;
  if (binding === 'affordability') return PDL_BINDING.headlineAffordability;
  if (binding === 'request') return PDL_BINDING.headlineRequest;
  return null;
}

function bindingDetail(
  binding: PdlMechanismSummary['binding'],
  table: number | null,
  bank: number | null,
): string | null {
  if (binding === 'legacy' && table != null && bank != null) {
    return PDL_BINDING.detailLegacy(fmt(table), fmt(bank));
  }
  if (binding === 'affordability' && table != null && bank != null) {
    return PDL_BINDING.detailAffordability(fmt(bank), fmt(table));
  }
  return null;
}

function bindingDetailShort(binding: PdlMechanismSummary['binding'], table: number | null): string | null {
  if (binding === 'legacy' && table != null) {
    return PDL_BINDING.detailLegacyShort(fmt(table));
  }
  return null;
}

/** Compact scoring chips for sub-60s adjudicator scan — not full trace tables. */
function scoringChipsFromMechanism(mechanism: PdlMechanismSummary): string[] {
  const chips: string[] = [];
  for (const line of mechanism.lines) {
    if (/^Scoring table \d/i.test(line)) {
      chips.push(formatPdlScoringChip('table', line));
      continue;
    }
    if (/^Income band:/i.test(line)) {
      chips.push(formatPdlScoringChip('band', line.replace(/^Income band:\s*/i, '')));
      continue;
    }
    if (/^Customer tier:/i.test(line)) {
      chips.push(formatPdlScoringChip('tier', line.replace(/^Customer tier:\s*/i, '')));
    }
  }
  return chips;
}

/**
 * Three-layer waterfall: request reference → two policy ceilings → final offer.
 * Omits PTI/TDS/ELOD step math — that stays in collapsed technical audit.
 */
export function buildPdlCapacityWaterfall(input: {
  mechanism: PdlMechanismSummary | null;
  requested: number | null;
  approved: number | null;
  why: string | null;
}): PdlCapacityWaterfallModel | null {
  const { mechanism, requested, approved } = input;
  const table = mechanism?.preApprovalLimit ?? null;
  const bank = mechanism?.bankCeiling ?? null;
  const finalOffer = approved ?? null;

  if (finalOffer == null && table == null && bank == null && requested == null) {
    return null;
  }

  const amounts = [requested, table, bank, finalOffer].filter(
    (n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0,
  );
  if (amounts.length === 0) return null;

  const scaleMax = Math.max(...amounts, 100);
  const binding = mechanism?.binding ?? null;
  const bars: PdlWaterfallBar[] = [];

  if (requested != null && requested > 0) {
    bars.push({
      id: 'requested',
      label: PDL_WATERFALL_BAR.requested.label,
      sublabel: PDL_WATERFALL_BAR.requested.sublabel,
      amount: requested,
      layer: 'observation',
      kind: 'requested',
      binding: false,
    });
  }

  if (table != null && table > 0) {
    bars.push({
      id: 'table',
      label: PDL_WATERFALL_BAR.table.label,
      sublabel: PDL_WATERFALL_BAR.table.sublabel,
      amount: table,
      layer: 'policy',
      kind: 'table_ceiling',
      binding: binding === 'legacy',
    });
  }

  if (bank != null && bank > 0) {
    bars.push({
      id: 'bank',
      label: PDL_WATERFALL_BAR.bank.label,
      sublabel: PDL_WATERFALL_BAR.bank.sublabel,
      amount: bank,
      layer: 'policy',
      kind: 'bank_ceiling',
      binding: binding === 'affordability',
    });
  }

  if (finalOffer != null && finalOffer >= 0) {
    bars.push({
      id: 'final',
      label: PDL_WATERFALL_BAR.final.label,
      sublabel: PDL_WATERFALL_BAR.final.sublabel,
      amount: finalOffer,
      layer: 'decision',
      kind: 'final_offer',
      binding: false,
    });
  }

  return {
    bars,
    scaleMax,
    bindingHeadline: bindingHeadline(binding),
    bindingDetail: bindingDetail(binding, table, bank),
    bindingDetailShort: bindingDetailShort(binding, table),
    scoringChips: mechanism ? scoringChipsFromMechanism(mechanism) : [],
    showBankPath: bank != null && table != null,
  };
}
